import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import {
  applyResolvedTheme,
  DARK_QUERY,
  readStoredMode,
  resolveTheme,
  storeMode,
  type ResolvedTheme,
  type ThemeMode,
} from './theme-mode';

/**
 * React binding for the theme-mode capability. Holds the chosen mode, applies the
 * resolved theme to <html>, persists the choice, and — while following the system —
 * tracks OS `prefers-color-scheme` changes. All the pure logic lives in `theme-mode`.
 */

interface ThemeContextValue {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(readStoredMode()),
  );

  // Re-apply + persist whenever the mode changes.
  useEffect(() => {
    const resolved = resolveTheme(mode);
    setResolvedTheme(resolved);
    applyResolvedTheme(resolved);
    storeMode(mode);
  }, [mode]);

  // Track OS changes, but only while following the system.
  useEffect(() => {
    if (mode !== 'system' || typeof window === 'undefined') return;
    const query = window.matchMedia(DARK_QUERY);
    const syncFromSystem = () => {
      const resolved: ResolvedTheme = query.matches ? 'dark' : 'light';
      setResolvedTheme(resolved);
      applyResolvedTheme(resolved);
    };
    query.addEventListener('change', syncFromSystem);
    return () => query.removeEventListener('change', syncFromSystem);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, resolvedTheme, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
